import { useState, useEffect } from 'react';
import { Download, X, Zap, Bell, WifiOff, Share, PlusSquare } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Detect iOS Safari
function isIOSSafari(): boolean {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const notChrome = !/CriOS/.test(ua);
  const notFirefox = !/FxiOS/.test(ua);
  return iOS && webkit && notChrome && notFirefox;
}

// Check if running in standalone mode (already installed)
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true)
  );
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isDismissed] = useState(() => {
    return localStorage.getItem('pwa-prompt-dismissed') === 'true';
  });

  useEffect(() => {
    // Skip if already dismissed or already installed as PWA
    if (isDismissed || isStandalone()) {
      return;
    }

    // Check for iOS Safari
    if (isIOSSafari()) {
      setIsIOS(true);
      // Show prompt after a delay on iOS
      const timer = setTimeout(() => setIsVisible(true), 3000);
      return () => clearTimeout(timer);
    }

    // For Android/Chrome - listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setIsVisible(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isDismissed]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  const handleRemindLater = () => {
    setIsVisible(false);
    sessionStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if dismissed, not visible, or (not iOS and no deferred prompt)
  if (!isVisible || isDismissed || (!isIOS && !deferredPrompt)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-96">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-cyan-500 to-teal-500 p-4 text-white">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Bezárás"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <img 
              src="/icons/tremoria-icon-source.png" 
              alt="Tremoria" 
              className="w-12 h-12 rounded-xl shadow-sm"
            />
            <div>
              <h3 className="font-semibold text-lg">Tremoria alkalmazás</h3>
              <p className="text-sm text-white/80">Telepítsd a gyorsabb elérésért</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Zap className="w-4 h-4 text-green-600" />
            </div>
            <span className="text-gray-700 dark:text-gray-300">Gyorsabb betöltés</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <WifiOff className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-gray-700 dark:text-gray-300">Offline elérhetőség</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="p-1.5 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
              <Bell className="w-4 h-4 text-cyan-600" />
            </div>
            <span className="text-gray-700 dark:text-gray-300">Push értesítések</span>
          </div>
        </div>

        {/* iOS Instructions */}
        {isIOS && (
          <div className="px-4 pb-2">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Telepítés lépései:
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center justify-center w-5 h-5 bg-cyan-100 dark:bg-cyan-900/50 rounded text-xs font-medium text-cyan-700 dark:text-cyan-300">1</span>
                <span>Nyomd meg a</span>
                <Share className="w-4 h-4 text-cyan-600" />
                <span>Megosztás gombot</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center justify-center w-5 h-5 bg-cyan-100 dark:bg-cyan-900/50 rounded text-xs font-medium text-cyan-700 dark:text-cyan-300">2</span>
                <span>Válaszd a</span>
                <PlusSquare className="w-4 h-4 text-cyan-600" />
                <span>"Kezdőképernyőre"</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-4 pt-2 flex items-center gap-3">
          <Button variant="outline" onClick={handleRemindLater} className="flex-1">
            Később
          </Button>
          {isIOS ? (
            <Button onClick={handleDismiss} className="flex-1 gap-2 bg-cyan-600 hover:bg-cyan-700">
              Értem
            </Button>
          ) : (
            <Button onClick={handleInstall} className="flex-1 gap-2 bg-cyan-600 hover:bg-cyan-700">
              <Download className="w-4 h-4" />
              Telepítés
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
